import type { Project } from "@storytell/types";
import { createSignal } from "solid-js";
import { projectCreatorLabel } from "~/components/projects/projectCreatorLabel";
import { useI18n } from "~/i18n/context";

type ProjectSortOption = "most_recent" | "create_date";

/**
 * Whether a project answers a lowercased, trimmed search query. The creator is matched under the
 * label the picker shows for it, so a project the system created is found by the product name
 * rather than the creator name the control plane sends.
 */
export function projectMatchesSearch(
  project: Pick<Project, "name" | "description" | "createdByName">,
  query: string,
  systemCreatorLabel: string
): boolean {
  return (
    project.name.toLowerCase().includes(query) ||
    project.description.toLowerCase().includes(query) ||
    projectCreatorLabel(project.createdByName, systemCreatorLabel).toLowerCase().includes(query)
  );
}

/**
 * Search and sort state for the project pickers in the copy and move modals. Search matches a
 * project's name, description, or creator; "most_recent" orders by last modification newest first,
 * "create_date" by creation oldest first.
 */
export function useProjectFilterAndSort(projects: () => Project[]) {
  const { t } = useI18n();
  const [searchQuery, setSearchQuery] = createSignal("");
  const [sortOption, setSortOption] = createSignal<ProjectSortOption>("most_recent");

  const filteredProjects = () => {
    const query = searchQuery().toLowerCase().trim();
    const allProjects = projects();
    if (!query) return allProjects;

    const systemCreatorLabel = t("project_created_by_storytell");
    return allProjects.filter((project) => projectMatchesSearch(project, query, systemCreatorLabel));
  };

  const sortedProjects = () => {
    const sorted = [...filteredProjects()];

    if (sortOption() === "most_recent") {
      sorted.sort((a, b) => Date.parse(b.modifiedAt) - Date.parse(a.modifiedAt));
    } else {
      sorted.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
    }

    return sorted;
  };

  return {
    filteredAndSortedProjects: sortedProjects,
    searchQuery,
    setSearchQuery,
    sortOption,
    setSortOption,
  };
}
